/**
 * WhatsappChatButton — the public site's WhatsApp entry point.
 *
 * A plain `wa.me` click-to-chat link, NOT an API call. Its entire purpose is
 * to get the VISITOR to send the first message, which opens Meta's 24h
 * free-form customer-service window (see
 * docs/superpowers/specs/2026-08-04-whatsapp-ai-agent-inbox-design.md §3).
 * No template approval, no cost.
 *
 * The number comes from the public env var `WHATSAPP_PUBLIC_NUMBER`, which
 * reaches the client through the root loader's `ENV` (see
 * app/.server/utils/env.ts `getEnv()` — the same mechanism already used for
 * `SUPABASE_URL`/`SUPABASE_ANON_KEY`/`FEATURE_LEADS`). We read it here via
 * `useRouteLoaderData('root')`, the same pattern `CopyProvider` uses to pull
 * `copyOverrides` off the root loader (see app/components/copy/copy-context.tsx).
 *
 * Renders nothing if the number isn't configured — never a broken link.
 */
import { useRouteLoaderData } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import { trackEvent, EventCategory, EventAction } from '~/utils/analytics';
import {
  buildWhatsappLink,
  buildWhatsappPrefill,
  isWhatsappNumberConfigured,
  type WhatsappPrefillLocale,
  type WorkspaceWhatsappContext,
} from '~/utils/whatsapp-link';

interface WhatsappChatButtonProps {
  /** Pass this on a workspace detail page to prefill a space-specific message. */
  workspace?: WorkspaceWhatsappContext;
  className?: string;
}

type RootLoaderEnvShape = { ENV?: { WHATSAPP_PUBLIC_NUMBER?: string } } | undefined;

function toWhatsappLocale(language: string | undefined): WhatsappPrefillLocale {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'es';
}

export function WhatsappChatButton({ workspace, className }: WhatsappChatButtonProps) {
  const rootData = useRouteLoaderData('root') as RootLoaderEnvShape;
  const number = rootData?.ENV?.WHATSAPP_PUBLIC_NUMBER;
  const { i18n } = useTranslation();

  if (!isWhatsappNumberConfigured(number)) {
    return null;
  }

  const locale = toWhatsappLocale(i18n?.language);
  const message = buildWhatsappPrefill(locale, workspace);
  const href = buildWhatsappLink({ number: number as string, message });
  const label = locale === 'en' ? 'Chat with us on WhatsApp' : 'Chatea con nosotros por WhatsApp';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // GTM: numerator of the test group's click-through (page_view carries wa_chat_variant).
      onClick={() =>
        trackEvent({
          category: EventCategory.NAVIGATION,
          action: EventAction.WHATSAPP_CHAT_CLICK,
          label: workspace ? 'workspace_detail' : 'generic',
        })
      }
      aria-label={label}
      title={label}
      className={
        className ??
        // Bottom-RIGHT — the spot the web ChatWidget occupies for the control group. In the
        // A/B (option 1) each visitor gets exactly one chat entry, and it should sit where
        // users expect the chat to live.
        'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#25D366]'
      }
    >
      <WhatsappGlyph className="h-7 w-7" />
    </a>
  );
}

function WhatsappGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.004 3C9.377 3 4 8.373 4 15c0 2.34.65 4.522 1.78 6.393L4 29l7.803-1.75A11.93 11.93 0 0 0 16.004 27C22.63 27 28 21.627 28 15S22.63 3 16.004 3Zm0 21.75a9.7 9.7 0 0 1-4.95-1.352l-.355-.21-4.63 1.038 1.06-4.51-.232-.37A9.7 9.7 0 0 1 5.25 15c0-5.93 4.824-10.75 10.754-10.75S26.75 9.07 26.75 15 20.934 24.75 16.004 24.75Zm5.86-7.44c-.32-.16-1.9-.938-2.194-1.045-.294-.108-.508-.16-.722.16-.213.32-.828 1.045-1.015 1.26-.187.213-.374.24-.694.08-.32-.16-1.35-.497-2.573-1.586-.951-.848-1.594-1.895-1.781-2.215-.187-.32-.02-.493.14-.652.144-.144.32-.374.48-.56.16-.187.213-.32.32-.534.107-.213.053-.4-.027-.56-.08-.16-.722-1.74-.99-2.383-.26-.626-.526-.54-.722-.55l-.615-.01c-.213 0-.56.08-.854.4-.294.32-1.12 1.094-1.12 2.668 0 1.574 1.147 3.094 1.307 3.307.16.213 2.257 3.446 5.468 4.833.764.33 1.36.527 1.826.674.767.244 1.465.21 2.017.127.615-.092 1.9-.777 2.168-1.527.267-.75.267-1.393.187-1.527-.08-.133-.293-.213-.613-.373Z" />
    </svg>
  );
}
